/**
 * Analytics Helper Functions
 *
 * Provides typed tracking functions for user actions in the Portal Cliente.
 * Uses Application Insights under the hood via trackEvent from appInsights.ts
 *
 * All tracking functions are gated by LGPD consent - no data is sent
 * unless the user has explicitly accepted via the ConsentBanner.
 */

import { trackEvent } from './appInsights'

// Re-export consent utilities so consumers can import from a single module
export { hasAnalyticsConsent, setAnalyticsConsent, hasConsentChoice } from './appInsights'

// ========================================
// User Actions
// ========================================

/**
 * Track user login attempt
 * @param success - Whether the login was successful
 * @param method - Authentication method used (default: 'password')
 */
export function trackLogin(success: boolean, method: string = 'password') {
  trackEvent('user.login', {
    success: String(success),
    method,
  })
}

/**
 * Track user logout
 */
export function trackLogout() {
  trackEvent('user.logout', {})
}

// ========================================
// RPS Actions
// ========================================

/**
 * Track RPS approval
 * @param empresaId - Company ID (CodigoEmpresaFat)
 * @param duplicata - Duplicata number
 */
export function trackRPSApproval(empresaId: string, duplicata: string) {
  trackEvent('rps.approved', {
    empresa_id: empresaId,
    duplicata,
  })
}

/**
 * Track RPS rejection
 * @param empresaId - Company ID (CodigoEmpresaFat)
 * @param duplicata - Duplicata number
 * @param motivo - Rejection reason
 */
export function trackRPSRejection(empresaId: string, duplicata: string, motivo: string) {
  trackEvent('rps.rejected', {
    empresa_id: empresaId,
    duplicata,
    motivo,
  })
}

// ========================================
// Profile Actions
// ========================================

/**
 * Track profile update
 * @param fields - Which fields were updated (e.g. 'nome', 'senha', 'imagem')
 */
export function trackProfileUpdate(fields: string[]) {
  trackEvent('profile.updated', {
    fields: fields.join(','),
  })
}

// ========================================
// HR Request Actions
// ========================================

/**
 * Track HR request submission
 * @param tipo - Request type (admissao, demissao, orcamento, alteracao)
 * @param empresaId - Company ID or CNPJ
 */
export function trackHRRequest(tipo: string, empresaId: string) {
  trackEvent('hr.request.submitted', {
    tipo,
    empresa_id: empresaId,
  })
}

// ========================================
// File Actions
// ========================================

/**
 * Track file download
 * @param fileType - Type of file (nfe, boleto, rps, contrato)
 * @param fileName - Name of the downloaded file
 */
export function trackFileDownload(fileType: string, fileName: string) {
  trackEvent('file.download', {
    file_type: fileType,
    file_name: fileName,
  })
}

// ========================================
// Navigation
// ========================================

/**
 * Track page navigation
 * @param from - Origin page/route
 * @param to - Destination page/route
 */
export function trackNavigation(from: string, to: string) {
  trackEvent('navigation', {
    from,
    to,
  })
}

// ========================================
// Feature Usage
// ========================================

/**
 * Track generic feature usage
 * @param feature - Feature name
 * @param action - Action performed
 */
export function trackFeatureUsage(feature: string, action: string) {
  trackEvent('feature.usage', {
    feature,
    action,
  })
}

// ========================================
// Search
// ========================================

/**
 * Track search actions
 * @param query - Search query (only length is tracked for privacy)
 * @param resultsCount - Number of results returned
 */
export function trackSearch(query: string, resultsCount: number) {
  trackEvent('search', {
    query_length: String(query.length),
    results_count: String(resultsCount),
  })
}
